import { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    num: '01',
    title: 'Inspection & Assessment',
    desc: 'Our certified inspector performs a thorough on-site evaluation — documenting every issue, measuring damage extent, and identifying root causes before recommending any solution.',
    icon: 'ri-search-eye-line',
    align: 'right',
  },
  {
    num: '02',
    title: 'Detailed Estimate',
    desc: 'You receive a clear written estimate with no hidden fees, no vague line items, and zero pressure. Just an honest breakdown of exactly what needs to be done and what it costs.',
    icon: 'ri-file-text-line',
    align: 'left',
  },
  {
    num: '03',
    title: 'Project Scheduling',
    desc: 'We schedule at your convenience, communicate a precise timeline, and confirm every detail before work begins. Your time and property are treated with full respect.',
    icon: 'ri-calendar-check-line',
    align: 'right',
  },
  {
    num: '04',
    title: 'Professional Installation',
    desc: 'Our GAF Certified crew installs using premium materials and Fortification-certified techniques — built to withstand Gulf Coast weather, salt air, and hurricane-force wind for decades ahead.',
    icon: 'ri-hammer-line',
    align: 'left',
  },
  {
    num: '05',
    title: 'Final Walkthrough & Warranty',
    desc: 'We complete a full walkthrough with you, answer every question, and deliver written warranty documentation before we pack up and leave your property cleaner than we found it.',
    icon: 'ri-shield-check-line',
    align: 'right',
  },
];

const PROCESS_IMAGE = 'https://public.readdy.ai/ai/img_res/54154fe83ba2382021e3cda91c1b6304.jpg';

export default function ProcessSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="process" ref={ref} className="py-24 md:py-32 bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-cream hidden xl:block"></div>
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-200 hidden xl:block" style={{ right: '50%' }}></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col xl:flex-row gap-16 items-start">

          {/* Left — process steps */}
          <div className="flex-1">
            {/* Header */}
            <div className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-gold"></span>
                <span className="text-[11px] font-bold tracking-super uppercase text-gold">How It Works</span>
              </div>
              <h2 className="font-condensed font-bold text-5xl md:text-6xl text-charcoal uppercase leading-none">
                A Straightforward<br />
                <span className="font-serif font-normal italic text-gray-400 text-4xl md:text-5xl normal-case leading-tight">
                  Roofing Process
                </span>
              </h2>
            </div>

            {/* Steps */}
            <div className="relative flex flex-col gap-0">
              {/* Vertical connector */}
              <div className="absolute left-5 top-10 bottom-10 w-px bg-gradient-to-b from-gold/50 via-gold/20 to-transparent hidden sm:block"></div>

              {STEPS.map((step, i) => (
                <div key={step.num}
                  className={`flex gap-6 md:gap-10 p-5 md:p-6 rounded-xl group hover:bg-gray-50 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 120}ms`, transitionDuration: '700ms' }}>

                  {/* Step circle */}
                  <div className="shrink-0 flex flex-col items-center gap-0">
                    <div className="w-10 h-10 flex items-center justify-center border-2 border-gold/30 bg-white rounded-full group-hover:border-gold group-hover:bg-gold/8 transition-all relative z-10">
                      <i className={`${step.icon} text-gold text-xs`}></i>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1.5 pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-condensed font-bold text-4xl text-gray-100 leading-none select-none group-hover:text-gold/12 transition-colors">
                        {step.num}
                      </span>
                      <h3 className="font-condensed font-bold text-lg text-charcoal uppercase tracking-wide">{step.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed font-light">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className={`mt-8 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start justify-between gap-4 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="font-serif italic text-gray-400 text-lg max-w-xs leading-snug">"From inspection to finished roof in under a week."</p>
              <a href="#estimate"
                onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="shrink-0 inline-flex items-center gap-3 bg-gold text-ink font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap">
                Start the Process
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* Right — sticky image */}
          <div className={`w-full xl:w-[400px] shrink-0 sticky top-28 transition-all duration-800 delay-200 hidden xl:block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '200ms' }}>
            <div className="relative">
              <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden">
                <img src={PROCESS_IMAGE} alt="All Done Roofing team at work" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 ring-1 ring-gold/15 rounded-lg pointer-events-none"></div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-5 bg-ink border border-gold/25 rounded-lg px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0">
                    <i className="ri-medal-2-fill text-gold text-xl"></i>
                  </div>
                  <div>
                    <p className="font-condensed font-bold text-white text-sm uppercase">GAF Certified</p>
                    <p className="text-[10px] text-white/35 tracking-widest uppercase">Fortification Certified</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
